import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import UserService2 from '@/services/UserService2'

const getUserDisplayName = user => {
	const name = user?.nazwa || user?.name || '-'
	const alternativeName = user?.alternative_name || user?.initials
	return alternativeName ? `${name} (${alternativeName})` : name
}

export default function useUserUpdatePage() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()
	const [user, setUser] = useState(null)
	const [loading, setLoading] = useState(true)
	const [submitting, setSubmitting] = useState(false)

	const handleCancel = useCallback(() => {
		navigate('/system/uzytkownicy')
	}, [navigate])

	const loadUser = useCallback(async () => {
		setLoading(true)
		const result = await UserService2.getById(id)

		if (result.error || !result.data) {
			await Swal.fire({
				title: t('notifications.errorTitle'),
				text: result.error || t('settingsPage.users.messages.loadFailed'),
				icon: 'error',
			})
			navigate('/system/uzytkownicy')
			return
		}

		setUser(result.data)
		setLoading(false)
	}, [id, navigate, t])

	useEffect(() => {
		if (id) loadUser()
	}, [id, loadUser])

	const handleSubmit = useCallback(async payload => {
		if (submitting || !id) return false

		setSubmitting(true)
		const result = await UserService2.update(id, payload)

		if (!result.success) {
			const emailTaken = `${result.error || ''}` === 'A user with this email already exists'
			await Swal.fire({
				title: t('notifications.errorTitle'),
				text: emailTaken
					? t('settingsPage.users.validation.emailTaken')
					: (result.error || t('settingsPage.users.messages.updateFailed')),
				icon: 'error',
			})
			setSubmitting(false)
			return false
		}

		const displayName = getUserDisplayName(
			result.data || { ...user, ...payload }
		)

		await Swal.fire({
			title: t('notifications.successTitle'),
			text: t('settingsPage.users.messages.updatedSuccess', { name: displayName }),
			icon: 'success',
		})

		setSubmitting(false)
		navigate('/system/uzytkownicy')
		return true
	}, [id, navigate, submitting, t, user])

	const uploadUserFile = useCallback(async (file, onUploadProgress) => {
		if (!id) return { success: false, error: t('settingsPage.users.files.messages.uploadFailed') }

		const result = await UserService2.uploadUserFile(id, file, onUploadProgress)
		if (result.success && result.data) {
			setUser(result.data)
		}
		return result
	}, [id, t])

	const downloadUserFile = useCallback(fileUuid => {
		if (!id) return Promise.resolve({ success: false, error: t('settingsPage.users.files.messages.downloadFailed') })
		return UserService2.downloadUserFile(id, fileUuid)
	}, [id, t])

	const deleteUserFile = useCallback(async fileUuid => {
		if (!id) return { success: false, error: t('settingsPage.users.files.messages.deleteFailed') }

		const result = await UserService2.deleteUserFile(id, fileUuid)
		if (result.success) {
			if (result.data) {
				setUser(result.data)
			} else {
				setUser(prev => prev ? {
					...prev,
					pliki: (prev.pliki || []).filter(file => file.fileUuid !== fileUuid),
				} : prev)
			}
		}
		return result
	}, [id, t])

	return {
		handleCancel,
		handleSubmit,
		loading,
		submitting,
		user,
		uploadUserFile,
		downloadUserFile,
		deleteUserFile,
	}
}
